<template>
    <vx-modal :visible="visible" @update:visible="updateVisible($event)" size="md" padding="standard">
        <template v-slot:header>
            <div class="text-2xl">Move to Another OU</div>
        </template>

        <div class="flex flex-col gap-4">
            <vx-alert v-if="error" color="danger" :closeable="false" shadow data-test="error">
                {{ error }}
            </vx-alert>
            <vx-alert v-if="props.group.name" :closeable="false" shadow>
                Moving <strong class="font-bold">{{ props.group.name }}</strong> group
            </vx-alert>

            <div>
                <vx-label size="lg">Organizational Unit</vx-label>
                <select-org-unit
                    v-model="targetOrgUnitModel"
                    relative-dropdown
                    size="lg"
                    placeholder=""
                    data-test="org-unit-selectable"
                ></select-org-unit>
            </div>
        </div>

        <template v-slot:footer>
            <div class="flex flex-col-reverse gap-4 md:flex-row">
                <vx-button
                    @click="updateVisible(false)"
                    type="button"
                    data-test="cancel-button"
                    class="grow md:w-20 md:grow-0"
                    size="lg"
                    color="muted"
                >
                    Cancel
                </vx-button>
                <vx-button
                    @click="submit"
                    data-test="move-button"
                    :loading="submitting"
                    :disabled="!targetOrgUnitModel"
                    type="button"
                    class="grow"
                    size="lg"
                    color="primary"
                >
                    Move <font-awesome-icon widthAuto :icon="faCircleArrowUpRight"></font-awesome-icon>
                </vx-button>
            </div>
        </template>
    </vx-modal>
</template>

<script setup>
import { ref } from 'vue';
import { VxAlert, VxButton, VxLabel, VxModal } from '@voxie/frontend-components';
import { orgUnitGroupsClient } from '~/services';
import { useToasted } from '~/composables/useToasted';
import { faCircleArrowUpRight } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { catchRequestError } from '~/utils/request';
import SelectOrgUnit from '~/components/general/SelectOrgUnit.vue';

const visible = defineModel('visible', {
    type: Boolean,
    default: true,
});

const props = defineProps({
    teamId: {
        type: Number,
        required: true,
    },
    sourceOrgUnitId: {
        type: Number,
        required: true,
    },
    group: {
        type: Object,
        required: true,
    },
});

const emit = defineEmits(['moved']);

const toasted = useToasted();
const submitting = ref(false);
const error = ref('');
const targetOrgUnitModel = ref(null);

const updateVisible = (isVisible) => {
    error.value = '';
    targetOrgUnitModel.value = null;
    visible.value = isVisible;
};

const submit = async () => {
    if (submitting.value) {
        return;
    }

    error.value = '';
    submitting.value = true;

    try {
        await orgUnitGroupsClient.removeOrgUnitGroup(props.teamId, props.sourceOrgUnitId, props.group.group_id);
        await orgUnitGroupsClient.addOrgUnitGroup(props.teamId, targetOrgUnitModel.value.id, props.group.group_id);

        emit('moved', {
            group_id: props.group.group_id,
            new_org_unit_id: targetOrgUnitModel.value.id,
        });

        toasted.success(`${props.group.name} group moved successfully`);
        updateVisible(false);
    } catch (e) {
        error.value = catchRequestError(e);
    } finally {
        submitting.value = false;
    }
};
</script>
